import type { Guide } from './types.js';

/**
 * The guide shown to VGS users, including administrators. It covers the internal
 * side of the platform — cost build-up, markup, internal notes, administration — and
 * is never rendered for a customer account.
 */
export const VGS_GUIDE: Guide = {
  title: 'VGS user and administrator guide',
  subtitle:
    'Running Reimbursable Variable Service and Repair work orders end to end: intake, investigation, pricing, authorization, execution, closeout, and administration.',
  audience:
    'Written for VGS program managers, facility managers, staff, and system administrators.',
  video: {
    title: 'Watch the walkthrough (20 minutes)',
    description:
      'A slow, captioned recording of a facility manager taking a work order from entry to Closed — quote and submit to USPS, USPS approval, PR/PO, schedule, complete and close — followed by an emergency authorization. Recorded on demo data.',
    src: '/walkthrough/facility-manager-walkthrough.mp4',
  },
  sections: [
    {
      id: 'overview',
      title: '1. How the platform is organized',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Every repair is a work order that belongs to one facility, which in turn belongs to one task order. The task order carries the commercial configuration — the Reimbursable Expense markup rate and the response targets — and the facility code becomes part of every work order number issued there, for example RE-TUL-000012.',
        },
        {
          kind: 'paragraph',
          text: 'A work order moves through a fixed state machine. The permitted moves are enforced in the application and again by a database trigger, so an unexpected sequence fails rather than producing a record that cannot be explained later. Status history, approvals, and the audit log are append-only.',
        },
        {
          kind: 'note',
          variant: 'warning',
          title: 'The one rule that governs everything else',
          text: 'Internal cost, markup mechanics, internal notes, and internal attachments are never visible to USPS. Customer-facing screens, reports, exports, and emails are built from views and explicitly selected fields, so nothing internal reaches USPS by accident. Anything you mark internal stays internal; anything you mark customer-visible is, in effect, published to USPS.',
        },
      ],
    },
    {
      id: 'roles',
      title: '2. VGS roles',
      blocks: [
        {
          kind: 'table',
          headers: ['Role', 'Scope of the role'],
          rows: [
            [
              'VGS System Administrator',
              'Full access: users, task orders and facilities, repair categories, approval authority, audit log, and all operational work. Deliberately excludes proposal approval.',
            ],
            [
              'VGS Program Manager',
              'Runs assigned task orders end to end: intake, investigation, proposals, submission to USPS, scheduling, completion, closing and cancelling, internal notes and costs, reports including financial values, and the audit log.',
            ],
            [
              'VGS Facility Manager',
              'The same operational work at their assigned facilities, without closing, cancelling, or submitting proposals to USPS, and without the audit log.',
            ],
            [
              'VGS Staff',
              'Views work at assigned facilities, records execution progress, comments, and uploads documents. No pricing authority.',
            ],
            [
              'Read-only / Auditor',
              'Reviews records, including internal detail, without changing anything.',
            ],
          ],
        },
        {
          kind: 'note',
          variant: 'info',
          title: 'No VGS account can approve a proposal',
          text: 'Approval authority belongs to USPS. It is not merely unchecked in the interface: no VGS role holds the approval permission at all, which makes “VGS cannot authorize its own price” a property of the data rather than a check somebody could forget.',
        },
        {
          kind: 'paragraph',
          text: 'Roles are rows in the database, not code, and each user is additionally scoped to specific facilities and task orders. A facility manager sees only their own facilities everywhere in the platform — lists, dashboards, reports, and exports alike.',
        },
      ],
    },
    {
      id: 'intake',
      title: '3. Intake: a new request arrives',
      blocks: [
        {
          kind: 'paragraph',
          text: 'USPS submits a work order and it lands as Submitted; VGS users for that facility are notified in the app and by email. VGS can also raise a work order on behalf of a facility where a request arrives by phone or email — the requestor contact fields record who actually asked.',
        },
        {
          kind: 'steps',
          items: [
            'Open the work order and read the problem description, location detail, priority, and any photographs attached by the requestor.',
            'Check the response target for that priority; targets are configured per task order.',
            'Assign the work internally using the assignment panel.',
            'Move the work order to Under VGS Review to show USPS it has been picked up. This is the visible acknowledgement — leaving it at Submitted tells the customer nothing.',
          ],
        },
      ],
    },
    {
      id: 'investigation',
      title: '4. Investigation',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Move the work order to Estimate in Progress and record the investigation. The findings, root cause, and recommended action are visible to USPS and are what justify the price you are about to propose; the estimated labour hours and the VGS internal notes are not.',
        },
        {
          kind: 'bullets',
          items: [
            'Date inspected and estimated labour hours.',
            'Findings, root cause, and recommended action — write these for a USPS reader.',
            'VGS internal notes — anything that is ours: supplier issues, pricing strategy, resourcing, previous history with the asset.',
            'Investigation photographs, uploaded as customer-visible or internal as appropriate.',
          ],
        },
      ],
    },
    {
      id: 'proposal',
      title: '5. Building and submitting the proposal',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The proposal is where cost becomes price. Add cost items by category — labour, materials, equipment, subcontractor, other — each with quantity, unit, and unit price. Every line is either customer-visible or internal.',
        },
        {
          kind: 'bullets',
          items: [
            'Customer-visible lines make up the subtotal USPS sees, and the markup is applied to exactly those lines, so USPS is never asked to authorize a total built partly from figures they cannot see.',
            'Internal lines are for VGS cost that is not being passed through as a visible line. USPS sees no trace of them.',
            'The markup rate is read from the task order configuration and snapshotted onto the proposal version. It is never typed in by hand, and a later configuration change does not rewrite a proposal already prepared.',
            'All money is held to the cent and rounded once; there is no floating-point arithmetic anywhere in the calculation.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'Write the scope of work and a findings summary — both are visible to USPS, and the scope is required before the proposal can be submitted. Internal notes on the proposal remain internal.',
        },
        {
          kind: 'note',
          variant: 'warning',
          title: 'Subcontracted work and quotes',
          text: 'A customer-visible subcontractor line cannot be submitted without the subcontractor’s own quote attached as a customer-visible document, because USPS reviews the quote alongside the proposal. A subcontract line kept internal needs no published quote — USPS sees no line it would explain — but those should be rare, since the agreement expects competition to be visible to the Contracting Officer.',
        },
        {
          kind: 'paragraph',
          text: 'Where competition applies, seek at least three quotes. If fewer are obtainable, raise a competition exception on the work order stating how many quotes you have and why; the Contracting Officer approves or rejects it in the platform, and a proposal cannot be authorized around an unresolved exception. A Contracting Officer may also require competition on a work order that would not otherwise call for it.',
        },
        {
          kind: 'paragraph',
          text: 'Submitting moves the work order to Awaiting USPS Approval, freezes the proposal version, and notifies the USPS approvers with authority for that value. A frozen version is never edited: a revision creates a new version and the earlier one is retained as superseded.',
        },
      ],
    },
    {
      id: 'authority',
      title: '6. Authorization thresholds you have to plan around',
      blocks: [
        {
          kind: 'table',
          headers: ['Total price to USPS', 'Routing'],
          rows: [
            ['Up to $5,000', 'COR or Contracting Officer may authorize.'],
            [
              'Over $5,000',
              'COR endorses first, then the Contracting Officer gives final approval.',
            ],
            [
              'Over $10,000',
              'The USPS Facilities Response Line must determine whether the work qualifies as a project before anyone authorizes it. Record the ticket number, the date called, and the outcome on the work order.',
            ],
          ],
        },
        {
          kind: 'paragraph',
          text: 'Thresholds are tested against the total price to USPS, markup included — the figure on the approval and the invoice. They are configuration, held per role and optionally per task order, and each approval stores the authority that applied when it was given.',
        },
        {
          kind: 'paragraph',
          text: 'Where the Response Line declines the work, the Contracting Officer may still authorize it at their discretion; that discretion is recorded separately from the approval. Be aware that re-pricing across a threshold re-routes the approval: a revision from $4,800 to $5,400 goes to the CO, not back to the COR who saw it first.',
        },
      ],
    },
    {
      id: 'decision',
      title: '7. The USPS decision',
      blocks: [
        {
          kind: 'bullets',
          items: [
            'Supplies / consumables (e.g. toilet paper, paper towels, equipment purchases) are entered as a work order too — choose "Supplies / consumables" as the type, list each item with quantity and unit price, and pick the payment method (Amazon Corp, McMaster-Carr or P-card — VGS only, USPS never sees it). The priced list becomes the quote with markup added; open the Proposal tab and "Save and submit to USPS". The same dollar thresholds apply. After approval there is no Procurement Stage: click "Order placed" under Receipt & completion, then record the receipt when the goods arrive and close. "Repeat this order" on a closed supplies order pre-fills the items, prices and payment method.',
            'Approved — the work order moves to Procurement Stage. An administrator records the PR and PO (or Paid by P-card) under Schedule & completion → Procurement; you are emailed to schedule as soon as that is done. Emergency/Urgent work may be scheduled and completed straight away.',
            'Revision requested — the work order returns to Estimate in Progress. Read the comment, prepare a new version, and resubmit.',
            'Rejected — the work order is closed out as Rejected with the reason recorded. Do not perform the work.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'The decision is emailed and appears in notifications. If a proposal is sitting unactioned, comment on the work order rather than chasing outside the platform, so the chase is part of the record.',
        },
      ],
    },
    {
      id: 'execution',
      title: '8. Scheduling and performing the work',
      blocks: [
        {
          kind: 'steps',
          items: [
            'Record the schedule: the scheduled date and the time window are visible to USPS, as are notes you write for them. The subcontractor name and internal scheduling detail are not.',
            'Move to Repair Scheduled, then to Work in Progress when the crew is on site. The actual start is captured, which is what makes cycle-time reporting meaningful.',
            'Use On Hold if progress stops — waiting on parts, access, or a USPS decision. A reason is required, and releasing the hold returns the work order to exactly the stage it interrupted rather than pushing it forward.',
            'Keep documents current as you go; photographs uploaded during the work are far easier to obtain then than afterwards.',
          ],
        },
      ],
    },
    {
      id: 'completion',
      title: '9. Completion and closeout',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Completion documentation is mandatory: a work order cannot reach Completed without it. Record what was performed, the date and time completed, any follow-up recommendation, and any exceptions. Final notes for USPS are customer-visible; internal completion notes are not.',
        },
        {
          kind: 'bullets',
          items: [
            'The completion time cannot be in the future, cannot precede the work starting, and cannot precede the request being submitted. These are rejected rather than quietly adjusted, so cycle times stay trustworthy.',
            'Attach completion photographs if USPS has asked for them, and any close-out documentation.',
            'Record the invoice number once invoicing has been raised. It is VGS-editable, appears to USPS read-only, and is a required reporting field.',
            'Close the work order when everything is in place. Closing is restricted to program managers and administrators.',
          ],
        },
      ],
    },
    {
      id: 'documents',
      title: '10. Documents, comments, and the visibility boundary',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Every attachment and every comment carries a visibility. Customer-visible items appear to USPS immediately; internal items are visible only to VGS accounts holding the internal-view permission, and are excluded from customer views, reports, exports, and emails by construction rather than by filtering at the point of display.',
        },
        {
          kind: 'bullets',
          items: [
            'Document types: request photo, investigation photo, proposal backup, subcontractor quote, completion photo, invoice, other.',
            'Up to 25 MB per file, ten files per upload, in PDF, JPG, PNG, HEIC, WEBP, DOC, DOCX, XLS, XLSX, or CSV. The file’s declared type must match its extension.',
            'Files are held in private storage and served only through the application, so a link cannot be forwarded to someone who is not entitled to the work order.',
          ],
        },
        {
          kind: 'note',
          variant: 'warning',
          title: 'Assume customer-visible is permanent',
          text: 'Publishing something to USPS and removing it afterwards does not unsee it, and the upload remains in the audit log. Decide the visibility before you upload, not after.',
        },
      ],
    },
    {
      id: 'reports',
      title: '11. Reports',
      blocks: [
        {
          kind: 'bullets',
          items: [
            'Work order report — one row per work order with the full set of fields the agreement requires of USPS reporting, a column picker, and CSV export matching the columns you selected.',
            'Approvals report — one row per approval decision with the value, the decider, and the authority relied upon; this is the backup for monthly invoicing.',
            'Financial summary — VGS-facing values, available only to roles holding the financial reporting permission.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'All reports are scoped to the user: a facility manager’s export contains their facilities only. Before sending any export outside VGS, remember that the work order report is a customer-facing artifact — it carries the authorized price, not our cost build-up.',
        },
      ],
    },
    {
      id: 'administration',
      title: '12. Administration',
      blocks: [
        {
          kind: 'definitions',
          items: [
            {
              term: 'Users',
              description:
                'Invite-only. Create the account with its roles, facilities, and task orders; the invitee sets their own password from a single-use link, and nobody — administrators included — ever sets another person’s password. Re-invite reissues an expired link. Changing a password revokes that user’s other sessions.',
            },
            {
              term: 'Task orders and facilities',
              description:
                'One screen creates a task order together with the facility it covers, in a single transaction. Each task order carries its markup rate and response targets, plus the USPS reporting metadata on the facility — Property ID, Postal Service Area/Logistics, District/Division, and local contact name, phone, and email. A configuration change creates a new version; proposals already prepared keep the rate they were written under. The facility code cannot change once work orders exist there, because it is embedded in their numbers.',
            },
            {
              term: 'Repair categories',
              description:
                'The categories offered when a work order is raised. Deactivating one keeps it on existing work orders but hides it from new requests.',
            },
            {
              term: 'Approval authority',
              description:
                'The dollar ceilings by USPS role, optionally overridden per task order, and the value above which the Facilities Response Line is required. A change applies to proposals submitted afterwards; approvals already recorded keep the authority that applied at the time. Record the reason for every change — that is what a contract modification looks like in the audit record.',
            },
          ],
        },
        {
          kind: 'paragraph',
          text: 'The audit log lists every recorded action in order, filterable, and cannot be edited or removed. Use it when reconstructing what happened rather than relying on recollection.',
        },
      ],
    },
    {
      id: 'notifications',
      title: '13. Notifications and email',
      blocks: [
        {
          kind: 'paragraph',
          text: 'In-app notifications are the bell in the header. Email is queued and retried rather than sent inline, so a mail outage can never fail or roll back the work order action that triggered it; the delivery status of each message is recorded.',
        },
        {
          kind: 'bullets',
          items: [
            'Invitation to a new user.',
            'Password reset.',
            'Proposal awaiting approval, to the USPS approvers with authority for the value.',
            'Approval decision, back to VGS.',
            'New work order submitted, to the VGS users for that facility.',
          ],
        },
      ],
    },
    {
      id: 'security',
      title: '14. Security expectations',
      blocks: [
        {
          kind: 'bullets',
          items: [
            'Sign-in failures are deliberately generic, so the platform cannot be used to discover who holds an account.',
            'Sessions are held in HttpOnly cookies and can be revoked; changing your password signs out your other sessions.',
            'Every permission is enforced on the server. Hiding a button is a convenience, never the control.',
            'Do not share exports outside their intended recipients, and do not email internal cost detail to a USPS address — the platform’s boundary cannot protect a document once it has left the platform.',
          ],
        },
      ],
    },
  ],
};
